import { defineMessages } from "react-intl";

import { ensureSidebarIsClosed } from "@app/component/flows/MapOnboarding/utils";
import TourDescriptor from "@app/component/flows/types/TourDescriptor";

const title = defineMessages({
  step1: {
    id: "guides.mapOnboarding.introduction.step1",
    defaultMessage: "See all your data layered in one view",
  },
  step2: {
    id: "guides.mapOnboarding.introduction.step2",
    defaultMessage: "This is the Map Menu",
  },
  step3: {
    id: "guides.mapOnboarding.introduction.step3",
    defaultMessage: "These are Filters",
  },
  step4: {
    id: "guides.mapOnboarding.introduction.step4",
    defaultMessage: "This is the Toolbar",
  },
  step5: {
    id: "guides.mapOnboarding.introduction.step5",
    defaultMessage: "These are the Map Controls",
  },
  step6: {
    id: "guides.mapOnboarding.introduction.step6",
    defaultMessage: "Last, click here to open the list",
  },
  step7: {
    id: "guides.mapOnboarding.introduction.step7",
    defaultMessage: "This is the list of records shows all pins in view currently",
  },
});

const description = defineMessages({
  step1: {
    id: "guides.mapOnboarding.introduction.step1.description",
    defaultMessage:
      "📋 Companies, People, and Deals{br}" +
      "📂 Groups{br}" +
      "🗺 Territories{br}" +
      "🔥 Heat Maps + more!",
  },
  step2: {
    id: "guides.mapOnboarding.introduction.step2.description",
    defaultMessage:
      "Here you can:{br}" +
      "📍 Segment your accounts with custom pins{br}" +
      "🗺 View and manage rep territory boundaries{br}" +
      "📂 View and manage groups of accounts",
  },
  step3: {
    id: "guides.mapOnboarding.introduction.step3.description",
    defaultMessage:
      "Here you can:{br}" +
      "👁 Turn on/off Companies, People, and Deals{br}" +
      "⚡️ Apply quick filters in a single click{br}" +
      "💾 Build custom filters and save them for later",
  },
  step4: {
    id: "guides.mapOnboarding.introduction.step4.description",
    defaultMessage:
      "Here you can:{br}" +
      "📍️ Drop a pin to create a record{br}" +
      "🤑 Search for new business with Lead Finder{br}" +
      "🤠 Lasso to select pins in bulk, then make edits{br}" +
      "📸 Take a screen grab of your current view{br}" +
      "📏 Measure distances",
  },
  step5: {
    id: "guides.mapOnboarding.introduction.step5.description",
    defaultMessage:
      "Here you can: {br}" +
      "🎯 Jump to a specific location on the map{br}" +
      "🙋 Focus in to your current location {br}" +
      "🎚️ Zoom in and out {br}" +
      "🎨 Change your map theme",
  },
  step6: {
    id: "guides.mapOnboarding.introduction.step6.description",
    defaultMessage:
      "Here you can: {br}" +
      "📇 See a list of all pins in view{br}" +
      "✍️ Control the look and feel of pin previews{br}" +
      "💾 Download a .csv of the list",
  },
  step7: {
    id: "guides.mapOnboarding.introduction.step7.description",
    defaultMessage:
      "Here you can: {br}" +
      "🔍 Search for records in view {br}" +
      "⚙️ Access view controls for pin previews  {br}" +
      "↕️ Change sort criteria",
  },
});

export const introductionTour: TourDescriptor = {
  id: "map-onboarding-introduction",
  steps: [
    {
      id: "welcome",
      description: description.step1,
      onBefore: async () => {
        ensureSidebarIsClosed();
      },
      title: title.step1,
    },
    {
      id: "pins-menu",
      description: description.step2,
      selector:
        "div[class*=MapPage_container] div[class*=Toolbar_container] div[class*=Toolbar_menuItem]:first-child",
      title: title.step2,
    },
    {
      id: "filters",
      description: description.step3,
      selector:
        "div[class*=MapPage_container] div[class*=TopPanel_container] > div:first-child > div:first-child",
      title: title.step3,
    },
    {
      id: "map-controls",
      description: description.step5,
      selector: "div[class*=MapPage_container] div[class*=ToolSelectionPanel_mapControls]",
      title: title.step5,
    },
    {
      id: "toolbar",
      description: description.step4,
      onAfter: async () => {
        // ensuring it is closed before the next step
        ensureSidebarIsClosed();
      },
      selector: "div[class*=MapPage_container] div[class*=ToolSelectionPanel_mapTools]",
      title: title.step4,
    },
    {
      id: "open-records",
      description: description.step6,
      onAfter: async () => {
        const element = document.querySelector(
          "div[class*=MapPage_container] div[class*=TopCommandsBar_container] > div:nth-child(2) > button"
        );
        if (element) {
          (element as HTMLElement).click();
        }
      },
      selector:
        "div[class*=MapPage_container] div[class*=TopCommandsBar_container] > div:nth-child(2)",
      title: title.step6,
    },
    {
      id: "record-list",
      description: description.step7,
      onAfter: async () => {
        ensureSidebarIsClosed();
      },
      onBefore: () => {
        return new Promise<void>((resolve) => {
          // waiting for drawer animation to complete
          setTimeout(resolve, 250);
        });
      },
      selector: "div[class*=Sidebar_container] .ant-drawer-content",
      title: title.step7,
    },
  ],
};
